import React from "react";

export type CuadroMandoLoadToastPhase = "loading" | "done" | null;

type CuadroMandoLoadToastProps = {
  phase: CuadroMandoLoadToastPhase;
};

const LOADING_MESSAGES = [
  "Cargando datos",
  "Comprobando datos",
  "Emparejando nómina",
  "Calculando pagos",
];

function useAnimatedLoadingLabel(active: boolean) {
  const [messageIndex, setMessageIndex] = React.useState(0);
  const [dotCount, setDotCount] = React.useState(1);

  React.useEffect(() => {
    if (!active) {
      setMessageIndex(0);
      setDotCount(1);
      return;
    }

    const dotsTimer = window.setInterval(() => {
      setDotCount((prev) => (prev % 3) + 1);
    }, 450);

    const messageTimer = window.setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
      setDotCount(1);
    }, 2600);

    return () => {
      window.clearInterval(dotsTimer);
      window.clearInterval(messageTimer);
    };
  }, [active]);

  const message = LOADING_MESSAGES[messageIndex];
  const dots = ".".repeat(dotCount);
  return { message, dots, messageIndex };
}

/**
 * Aviso flotante del cuadro de mando (carga animada / listo).
 */
export function CuadroMandoLoadToast({ phase }: CuadroMandoLoadToastProps) {
  const [visible, setVisible] = React.useState(false);
  const loading = useAnimatedLoadingLabel(phase === "loading");

  React.useEffect(() => {
    if (!phase) {
      setVisible(false);
      return;
    }
    if (!visible) {
      const id = window.requestAnimationFrame(() => setVisible(true));
      return () => window.cancelAnimationFrame(id);
    }
  }, [phase, visible]);

  if (!phase) return null;

  const isLoading = phase === "loading";

  const toastClassName = [
    "cuadro-mando-load-toast",
    isLoading ? "cuadro-mando-load-toast--loading" : "cuadro-mando-load-toast--done",
    visible ? "cuadro-mando-load-toast--visible" : "",
    isLoading && visible ? "cuadro-mando-load-toast--corner-pulse" : "",
    !isLoading && visible ? "cuadro-mando-load-toast--done-in" : "",
    !isLoading && visible ? "cuadro-mando-load-toast--done-glow" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy={isLoading}
      className={toastClassName}
    >
      {isLoading ? (
        <>
          <span
            className="spinner-border spinner-border-sm cuadro-mando-load-toast__spinner"
            aria-hidden
          />
          <span
            className="cuadro-mando-load-toast__loading-text"
            aria-label={`${loading.message}${loading.dots}`}
          >
            <span
              key={loading.messageIndex}
              className="cuadro-toast-loading-message"
            >
              {loading.message}
            </span>
            <span className="cuadro-mando-load-toast__dots" aria-hidden>
              {loading.dots}
            </span>
          </span>
        </>
      ) : (
        <>
          <span className="cuadro-mando-load-toast__check" aria-hidden>
            <i className="fa-solid fa-circle-check" />
          </span>
          <div className="cuadro-mando-load-toast__done-text">
            <span className="cuadro-mando-load-toast__done-title">
              Datos cargados
            </span>
            <span className="cuadro-mando-load-toast__done-subtitle">
              El cuadro de mando está listo. Revisa los pagos.
            </span>
          </div>
        </>
      )}
      <style>{`
        .cuadro-mando-load-toast {
          position: fixed;
          top: 5.25rem;
          right: 1.5rem;
          z-index: 1100;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          pointer-events: none;
          max-width: min(400px, calc(100vw - 2rem));
          opacity: 0;
          transform: translateY(-8px);
          transition: opacity 0.28s ease, transform 0.28s ease;
        }

        .cuadro-mando-load-toast--visible {
          opacity: 1;
          transform: translateY(0);
        }

        .cuadro-mando-load-toast--loading {
          padding: 0.85rem 1.4rem;
          border-radius: 999px;
          font-size: 1rem;
          font-weight: 600;
          line-height: 1.35;
          color: #fafafa;
          background: #0a0a0a;
          border: 1px solid #404040;
          box-shadow:
            0 0 0 1px rgba(255, 255, 255, 0.06),
            0 12px 32px rgba(0, 0, 0, 0.35);
        }

        .cuadro-mando-load-toast--corner-pulse {
          animation: cuadroToastCornerPulse 2.4s ease-in-out infinite;
        }

        .cuadro-mando-load-toast--done {
          align-items: flex-start;
          padding: 1rem 1.25rem;
          border-radius: 14px;
          min-width: min(320px, calc(100vw - 2rem));
          max-width: min(360px, calc(100vw - 2rem));
          color: #fafafa;
          background: #22c55e;
          border: 1px solid #16a34a;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
        }

        .cuadro-mando-load-toast--done-in {
          animation: cuadroToastDoneIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }

        .cuadro-mando-load-toast--done-glow {
          animation: cuadroToastDoneGlow 2.2s ease-in-out infinite;
        }

        .cuadro-mando-load-toast__spinner {
          width: 1.15rem;
          height: 1.15rem;
          border-width: 2px;
          flex-shrink: 0;
          color: #fafafa;
        }

        .cuadro-mando-load-toast__loading-text {
          display: inline-flex;
          align-items: baseline;
          min-width: 12.5rem;
          color: #fafafa;
        }

        .cuadro-mando-load-toast__dots {
          display: inline-block;
          min-width: 1.1em;
          text-align: left;
        }

        .cuadro-mando-load-toast__check {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.18);
          color: #ffffff;
          flex-shrink: 0;
          font-size: 1.15rem;
        }

        .cuadro-mando-load-toast__done-text {
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
          flex: 1;
          min-width: 0;
        }

        .cuadro-mando-load-toast__done-title {
          font-size: 0.95rem;
          font-weight: 700;
          color: #fafafa;
          letter-spacing: -0.01em;
          line-height: 1.25;
        }

        .cuadro-mando-load-toast__done-subtitle {
          font-size: 0.8rem;
          font-weight: 500;
          color: #dcfce7;
          line-height: 1.45;
          white-space: normal;
          word-break: break-word;
        }

        .cuadro-toast-loading-message {
          animation: cuadroToastMessageSwap 0.4s ease;
        }

        @keyframes cuadroToastCornerPulse {
          0%,
          100% {
            box-shadow:
              0 0 0 1px rgba(255, 255, 255, 0.06),
              0 12px 32px rgba(0, 0, 0, 0.35);
          }
          50% {
            box-shadow:
              0 0 0 1px rgba(255, 255, 255, 0.1),
              0 14px 36px rgba(0, 0, 0, 0.42);
          }
        }

        @keyframes cuadroToastDoneIn {
          from {
            opacity: 0;
            transform: translateY(-10px) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes cuadroToastDoneGlow {
          0%,
          100% {
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
          }
          50% {
            box-shadow: 0 10px 28px rgba(0, 0, 0, 0.26);
          }
        }

        @keyframes cuadroToastMessageSwap {
          from {
            opacity: 0.35;
          }
          to {
            opacity: 1;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .cuadro-mando-load-toast--corner-pulse,
          .cuadro-mando-load-toast--done-glow {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
